// =====================================================================
// paginas/imoveis.js — Imóveis, prédios, proprietários e fotos.
//
// Fotos servem a um propósito concreto: mandar pro candidato no WhatsApp
// sem sair do sistema.
//
// A ocupação NÃO é um campo — é derivada dos contratos (vw_desempenho_imovel).
// Campo que depende de alguém lembrar de atualizar está errado metade do tempo.
// =====================================================================

import { sb } from '../../../../packages/ui/cliente.js';
import { icon, escapeHtml, tabela, badge, modal, cabecalho, checar } from '../ui.js';
import { moeda } from '../../../../packages/ui/format.js';

let filtro = '';

const STATUS = {
  disponivel: ['Disponível', 'azul'],
  alugado:    ['Alugado',    'verde'],
  manutencao: ['Manutenção', 'ambar'],
  inativo:    ['Inativo',    'cinza']
};

export async function pagImoveis(el, estado) {
  const [imoveis, predios, proprietarios, desempenho] = await Promise.all([
    sb.from('imoveis').select('*,predios(nome),proprietarios(nome)').order('codigo'),
    sb.from('predios').select('id,nome').order('nome'),
    sb.from('proprietarios').select('id,nome').eq('ativo', true).order('nome'),
    sb.from('vw_desempenho_imovel').select('imovel_id,taxa_ocupacao_pct,rendimento_liquido_pct,recebido_12m')
  ]);

  const lista = checar(imoveis);
  const listaPredios = checar(predios);
  const listaProps = checar(proprietarios);
  const desemp = new Map((desempenho.data || []).map(d => [d.imovel_id, d]));

  el.innerHTML = '';
  el.appendChild(cabecalho({
    titulo: 'Imóveis',
    busca: 'Buscar por código, endereço…',
    onBusca: (v) => { filtro = v; desenhar(); },
    acao: 'Novo imóvel',
    onAcao: () => formImovel(null, listaPredios, listaProps, () => pagImoveis(el, estado))
  }));

  // Barra de ações secundárias: prédios e proprietários vivem aqui dentro,
  // não merecem aba própria (são cadastros de apoio).
  const barra = document.createElement('div');
  barra.style.cssText = 'display:flex;gap:8px;margin-bottom:16px';
  barra.innerHTML = `
    <button id="b-predios">${icon('imovel')} Prédios (${listaPredios.length})</button>
    <button id="b-props">${icon('proprietario')} Proprietários (${listaProps.length})</button>`;
  el.appendChild(barra);
  barra.querySelector('#b-predios').onclick = () => formPredio(() => pagImoveis(el, estado), estado);
  barra.querySelector('#b-props').onclick  = () => formProprietario(() => pagImoveis(el, estado), estado);

  const corpo = document.createElement('div');
  el.appendChild(corpo);

  function desenhar() {
    const f = lista.filter(i => !filtro
      || (i.codigo || '').toLowerCase().includes(filtro)
      || (i.logradouro || '').toLowerCase().includes(filtro)
      || (i.predios?.nome || '').toLowerCase().includes(filtro));

    const div = document.createElement('div');
    div.innerHTML = tabela([
      { th: 'Código', td: i => `<strong>${escapeHtml(i.codigo)}</strong>` },
      { th: 'Prédio', td: i => escapeHtml(i.predios?.nome || '—') },
      { th: 'Tipo',   td: i => escapeHtml(i.tipo) },
      { th: 'Proprietário', td: i => i.origem === 'proprio'
          ? badge('próprio', 'azul')
          : escapeHtml(i.proprietarios?.nome || 'terceiro') },
      { th: 'Aluguel', cls: 'num', td: i => i.valor_aluguel_ref ? moeda(i.valor_aluguel_ref) : '—' },
      { th: 'Mercado', cls: 'num', td: i => i.valor_mercado ? moeda(i.valor_mercado) : '—' },
      { th: 'Ocupação', cls: 'num', td: i => {
          const d = desemp.get(i.id);
          if (!d || d.taxa_ocupacao_pct == null) return '—';
          const p = Number(d.taxa_ocupacao_pct);
          return badge(`${p}%`, p >= 90 ? 'verde' : p >= 60 ? 'ambar' : 'vermelho');
        } },
      { th: 'Rend. líq.', cls: 'num', td: i => {
          const d = desemp.get(i.id);
          return d?.rendimento_liquido_pct != null ? `${d.rendimento_liquido_pct}% a.a.` : '—';
        } },
      { th: 'Situação', td: i => {
          const [t, c] = STATUS[i.status] || [i.status, 'cinza'];
          return badge(t, c);
        } },
      { th: '', cls: 'acoes', td: i => `
          <button class="icone" data-fotos="${i.id}" title="Fotos">${icon('foto')}</button>
          <button class="icone" data-editar="${i.id}" title="Editar">${icon('editar')}</button>` }
    ], f, 'Nenhum imóvel cadastrado.');

    div.querySelectorAll('[data-editar]').forEach(b => b.onclick = () =>
      formImovel(lista.find(x => x.id === b.dataset.editar), listaPredios, listaProps,
                 () => pagImoveis(el, estado)));

    div.querySelectorAll('[data-fotos]').forEach(b => b.onclick = () =>
      telaFotos(lista.find(x => x.id === b.dataset.fotos), estado));

    corpo.querySelector('table, .vazio')?.remove();
    corpo.appendChild(div.firstElementChild);
  }

  desenhar();
}

// ─── Imóvel ───────────────────────────────────────────────────────────
function formImovel(im, predios, props, aoSalvar) {
  modal({
    titulo: im ? `Imóvel ${im.codigo}` : 'Novo imóvel',
    dados: im || {},
    campos: [
      { nome: 'codigo', label: 'Código', req: true, dica: 'ex.: AP-102, SALA 601' },
      { nome: 'tipo', label: 'Tipo', tipo: 'select',
        opcoes: ['apartamento', 'casa', 'sala', 'loja', 'galpao', 'kitnet', 'flat', 'vaga', 'terreno'] },
      { nome: 'predio_id', label: 'Prédio', tipo: 'select',
        opcoes: [{ valor: '', texto: '— sem prédio —' },
                 ...predios.map(p => ({ valor: p.id, texto: p.nome }))] },
      { nome: 'origem', label: 'Origem', tipo: 'select',
        opcoes: [{ valor: 'proprio', texto: 'Imóvel próprio' },
                 { valor: 'terceiro', texto: 'De terceiro (gera repasse)' }],
        dica: 'Define o destino do dinheiro: caixa ou repasse.' },
      { nome: 'proprietario_id', label: 'Proprietário', tipo: 'select',
        opcoes: [{ valor: '', texto: '— a própria imobiliária —' },
                 ...props.map(p => ({ valor: p.id, texto: p.nome }))] },
      { nome: 'cep', label: 'CEP' },
      { nome: 'logradouro', label: 'Logradouro', full: true },
      { nome: 'numero', label: 'Número' },
      { nome: 'complemento', label: 'Complemento' },
      { nome: 'bairro', label: 'Bairro' },
      { nome: 'cidade', label: 'Cidade' },
      { nome: 'uf', label: 'UF' },
      { nome: 'area_m2', label: 'Área (m²)', tipo: 'number', step: '0.01' },
      { nome: 'quartos', label: 'Quartos', tipo: 'number' },
      { nome: 'banheiros', label: 'Banheiros', tipo: 'number' },
      { nome: 'vagas', label: 'Vagas', tipo: 'number' },
      { nome: 'valor_aluguel_ref', label: 'Valor do aluguel', tipo: 'number', step: '0.01' },
      { nome: 'valor_condominio', label: 'Condomínio', tipo: 'number', step: '0.01' },
      { nome: 'valor_iptu_mensal', label: 'IPTU mensal', tipo: 'number', step: '0.01' },
      { nome: 'valor_mercado', label: 'Valor de mercado', tipo: 'number', step: '0.01' },
      // 'alugado' NÃO entra aqui: quem define isso é o contrato ativo, via
      // trigger no banco. Um campo que alguém precisa lembrar de atualizar
      // fica errado metade do tempo — e a taxa de ocupação sai mentirosa.
      { nome: 'status', label: 'Situação', tipo: 'select',
        opcoes: [{ valor: 'disponivel', texto: 'Disponível' },
                 { valor: 'manutencao', texto: 'Em manutenção' },
                 { valor: 'inativo', texto: 'Inativo' }],
        dica: 'Alugado é definido automaticamente pelo contrato ativo.' },
      { nome: 'observacoes', label: 'Observações', tipo: 'textarea', full: true }
    ],
    onSalvar: async (d) => {
      ['area_m2', 'quartos', 'banheiros', 'vagas', 'valor_aluguel_ref',
       'valor_condominio', 'valor_iptu_mensal', 'valor_mercado'].forEach(k => {
        d[k] = d[k] ? Number(d[k]) : (k.startsWith('valor_cond') || k.startsWith('valor_iptu') ? 0 : null);
      });
      if (im) checar(await sb.from('imoveis').update(d).eq('id', im.id));
      else    checar(await sb.from('imoveis').insert(d));
      aoSalvar();
    }
  });
}

function formPredio(aoSalvar, estado) {
  modal({
    titulo: 'Novo prédio',
    campos: [
      { nome: 'nome', label: 'Nome', req: true, full: true, dica: 'ex.: REGINA, MÁRIO CAMPOS 260' },
      { nome: 'tipo', label: 'Tipo', tipo: 'select',
        opcoes: ['residencial', 'comercial', 'misto', 'galpao', 'terreno'] },
      { nome: 'cep', label: 'CEP' },
      { nome: 'logradouro', label: 'Logradouro', full: true },
      { nome: 'numero', label: 'Número' },
      { nome: 'bairro', label: 'Bairro' },
      { nome: 'cidade', label: 'Cidade' },
      { nome: 'uf', label: 'UF' }
    ],
    onSalvar: async (d) => {
      checar(await sb.from('predios').insert({ ...d, tenant_id: estado.perfil.tenant_id }));
      aoSalvar();
    }
  });
}

function formProprietario(aoSalvar, estado) {
  modal({
    titulo: 'Novo proprietário',
    aviso: 'Proprietário não precisa ter login. O acesso ao portal é opcional e vem depois.',
    campos: [
      { nome: 'nome', label: 'Nome', req: true, full: true },
      { nome: 'tipo_pessoa', label: 'Tipo', tipo: 'select',
        opcoes: [{ valor: 'fisica', texto: 'Pessoa física' }, { valor: 'juridica', texto: 'Pessoa jurídica' }] },
      { nome: 'cpf_cnpj', label: 'CPF / CNPJ' },
      { nome: 'telefone', label: 'Telefone' },
      { nome: 'email', label: 'E-mail', tipo: 'email' },
      { nome: 'pix_chave', label: 'Chave Pix (repasse)', full: true },
      { nome: 'taxa_adm_pct', label: 'Taxa de administração (%)', tipo: 'number', step: '0.01',
        dica: 'Vazio = usa a taxa padrão da imobiliária.' }
    ],
    onSalvar: async (d) => {
      d.taxa_adm_pct = d.taxa_adm_pct ? Number(d.taxa_adm_pct) : null;
      checar(await sb.from('proprietarios').insert({ ...d, tenant_id: estado.perfil.tenant_id }));
      aoSalvar();
    }
  });
}

// ─── Fotos + envio por WhatsApp ───────────────────────────────────────
async function telaFotos(im, estado) {
  const fotos = checar(await sb.from('imovel_fotos')
    .select('*').eq('imovel_id', im.id).order('ordem'));

  // O bucket é privado. Assinamos cada URL (1h) só para exibir aqui.
  const urls = await Promise.all(fotos.map(f => urlFoto(f.url)));

  const fundo = document.createElement('div');
  fundo.className = 'modal-fundo';
  fundo.innerHTML = `
    <div class="modal">
      <header><h3>${icon('foto')} Fotos — ${escapeHtml(im.codigo)}</h3>
        <button class="icone" data-fechar>${icon('erro')}</button></header>
      <div class="corpo">
        <div class="miniaturas" id="galeria">
          ${urls.map(u => `<img src="${escapeHtml(u)}" alt="">`).join('')
            || '<p class="vazio">Nenhuma foto ainda.</p>'}
        </div>
        <label class="botao" style="justify-content:center">
          ${icon('upload')} Adicionar fotos
          <input type="file" id="arq" accept="image/*" multiple hidden>
        </label>
        <hr style="border:none;border-top:1px solid var(--borda);margin:4px 0">
        <label>Enviar as fotos por WhatsApp
          <input id="tel" placeholder="Telefone do candidato com DDD">
        </label>
        <button class="primaria" id="enviar" ${fotos.length ? '' : 'disabled'}>
          ${icon('whatsapp')} Enviar ${fotos.length} foto(s)
        </button>
      </div>
    </div>`;

  fundo.querySelectorAll('[data-fechar]').forEach(b => b.onclick = () => fundo.remove());
  fundo.onclick = (e) => { if (e.target === fundo) fundo.remove(); };

  fundo.querySelector('#arq').onchange = async (e) => {
    for (const file of e.target.files) {
      const path = `${estado.perfil.tenant_id}/${im.id}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.jpg`;
      const { error } = await sb.storage.from('imoveis').upload(path, file);
      if (error) return alert('Falha ao enviar: ' + error.message);
      await sb.from('imovel_fotos').insert({
        tenant_id: estado.perfil.tenant_id,
        imovel_id: im.id,
        url: path,
        ordem: fotos.length
      });
    }
    fundo.remove();
    telaFotos(im, estado);
  };

  fundo.querySelector('#enviar').onclick = async () => {
    const tel = fundo.querySelector('#tel').value;
    if (!tel) return alert('Informe o telefone.');
    const { data: { session } } = await sb.auth.getSession();
    const r = await fetch('/.netlify/functions/enviar-fotos-imovel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ imovel_id: im.id, telefone: tel })
    }).then(x => x.json());
    alert(r.ok ? 'Fotos enviadas.' : (r.error || 'Falha no envio'));
    if (r.ok) fundo.remove();
  };

  document.body.appendChild(fundo);
}

// O bucket é privado (foto de imóvel é material comercial, não conteúdo
// público). URL assinada de 1h só para exibir a miniatura na tela.
async function urlFoto(path) {
  const { data } = await sb.storage.from('imoveis').createSignedUrl(path, 3600);
  return data?.signedUrl || '';
}
